import React, { useState } from "react";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Breadcrumbs,
} from "@mui/material";
import { Settings, BuildOutlined } from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
// configuration files
import { EmployeeDetails } from "./EmployeeDetails";
import { EmployeeGroup } from "./EmployeeGroup";
import { themes } from "services/constants";
//customFilter
import { setFilterApi } from "services/Redux/userManagament";
import { FilterProvider } from "pages/FilterContext";
import { checkViewerPermission, checkEditorPermission } from "services/utils/HelperFunction";

const theme = createTheme({
  palette: {
    primary: {
      main: "#00D4AA",
    },
    secondary: {
      main: "#6c757d",
    },
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
});

const employeeSidebarWidth = 280;

export const EmployeeOnboard = () => {
  const [userProcess, setUserProcess] = useState(false);
  const [selectedItem, setSelectedItem] = useState("Employee");
  const sideBarOpen = true;
  const [showFullPage, setShowFullPage] = useState(false);
  const [taskProgress, setTaskProgress] = useState({
    active: false,
    progress: 0,
    message: "",
    status: null,
    title: "Bulk Reprocessing",
    initiatedBy: "System",
    estimatedTime: "",
    shiftCategoryName: "",
    taskId: null,
    created_at: null,
    updated_at: null,
    duration: null,
    image_url: null,
  });
  const { domain, userScreenAccess, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );

  const employeeModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Employee",
  );

  const accessibleItems = ["Employee", "Employee Group"].filter((name) =>
    checkViewerPermission(userScreenAccess?.module_details, "Employee", name),
  );
  const activeItem = accessibleItems.includes(selectedItem)
    ? selectedItem
    : accessibleItems[0];
  const canEditActiveItem = checkEditorPermission(
    userScreenAccess?.module_details, "Employee", activeItem,
  );

  const subModuleId = employeeModule?.submodules?.find(
    (sub) => sub.sub_module_name === activeItem,
  )?.sub_module_id;

  const dispatch = useDispatch();

  const employeeMenuItems = [
    {
      type: "tile",
      title: "Organization",
    },
    {
      type: "item",
      title: "Employee",
      key: "Employee",
      subtitle: "Employee",
      icon: <Settings />,
      color: "#6c757d",
    },
    {
      type: "item",
      title: "Employee Group",
      key: "Employee Group",
      subtitle: "Employee Group",
      icon: <BuildOutlined />,
      color: "#6c757d",
    },
  ];

  const handleEmployeeItemClick = (itemTitle) => {
    if (!accessibleItems.includes(itemTitle)) return;
    if (selectedItem === itemTitle) return;
    setSelectedItem(itemTitle);
    dispatch(setFilterApi(true));
    setShowFullPage(false);
  };

  const renderMainContent = () => {
    switch (activeItem) {
      case "Employee":
        return (
          <EmployeeDetails
            setUserProcess={setUserProcess}
            userProcess={userProcess}
            setTaskProgress={setTaskProgress}
            taskProgress={taskProgress}
          />
        );
      case "Employee Group":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <EmployeeGroup />
          </FilterProvider>
        );

      default:
        return <Typography>You do not have access to employee screens.</Typography>;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          height: "94.5vh",
          overflow: "hidden",
          backgroundColor: themes?.whiteColor,
        }}
      >
        <Box
          sx={{
            width: sideBarOpen ? employeeSidebarWidth : 0,
            transition: "width 0.3s ease",
            overflowX: "hidden",
            flexShrink: 0,
            mt: "10px",
            backgroundColor: themes?.whiteColor,
            borderRight: "1px solid #f5f5fd",
          }}
        >
          <List sx={{ p: 2 }}>
            {employeeMenuItems.filter((item) =>
              item.type === "tile" ? accessibleItems.length > 0 : accessibleItems.includes(item.key),
            ).map((item, index) =>
              item.type === "tile" ? (
                <Typography
                  key={`tile-${index}`}
                  variant="subtitle"
                  sx={{ fontWeight: 700, color: "#00D4AA", mt: 2, mb: 2 }}
                >
                  {item.title}
                </Typography>
              ) : (
                <ListItem key={item.title} disablePadding sx={{ mb: 1, mt: 1 }}>
                  <ListItemButton
                    onClick={() => handleEmployeeItemClick(item.key)}
                    sx={{
                      backgroundColor:
                        activeItem === (item.key ?? item.groupKey)
                          ? "#f0f8ff"
                          : "transparent",
                      borderRadius: 1,
                      p: 1,
                      "&:hover": { backgroundColor: "#f8f9fa" },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: "38px",
                        color:
                          activeItem === item.key ? "#00D4AA" : item.color,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {item.subtitle}
                      </Typography>
                    </Box>
                  </ListItemButton>
                </ListItem>
              ),
            )}
          </List>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            minWidth: 0,
            height: "100%",
            overflowY: "auto",
            backgroundColor: "#f5f5fd",
            transition: "all 0.3s ease",
          }}
        >
          {sideBarOpen && !showFullPage && (
            <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{ mb: 2 }}>
              <Typography>People</Typography>
              <Typography color={themes?.blueColor}>{activeItem || "Employee"}</Typography>
            </Breadcrumbs>
          )}

          <React.Fragment key={`${activeItem}-${canEditActiveItem}`}>
            {renderMainContent()}
          </React.Fragment>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
